import { replace } from 'lodash';
import numeral from 'numeral';

// ----------------------------------------------------------------------

export function fCurrency(number) {
  return numeral(number).format(Number.isInteger(number) ? '$0,0' : '$0,0.00');
}

export function fPercent(number) {
  return numeral(number / 100).format('0.0%');
}

export function fNumber(number) {
  return numeral(number).format();
}

export function fShortenNumber(number) {
  return replace(numeral(number).format('0.00a'), '.00', '');
}

export function fData(number) {
  return numeral(number).format('0.0 b');
}

export const vatFormats = {
  BE: { regex: /^\d{10}$/, maxLength: 10, exampleFormat: "BE 0123456789", vatPlaceholder: "9999999999" },
  AD: { regex: /^\d{10}$/, maxLength: 6, exampleFormat: "AD 123456", vatPlaceholder: "999999" },
  BG: { regex: /^\d{9,10}$/, minLength: 9, maxLength: 10, exampleFormat: "BG 123456789 or BG 1234567890", vatPlaceholder: "9999999999" },
  LB: { regex: /^\d{9,10}$/, minLength: 9, maxLength: 8, exampleFormat: "LB 12345678", vatPlaceholder: "99999999" },
  DK: { regex: /^\d{8}$/, maxLength: 8, exampleFormat: "DK 12345678", vatPlaceholder: "99999999" },
  EE: { regex: /^\d{9}$/, maxLength: 9, exampleFormat: "EE 123456789", vatPlaceholder: "999999999" },
  FI: { regex: /^\d{8}$/, maxLength: 8, exampleFormat: "FI 12345678", vatPlaceholder: "99999999" },
  ZW: { regex: /^\d{8}$/, maxLength: 8, exampleFormat: "ZW 12345678", vatPlaceholder: "99999999" },
  FR: { regex: /^[0-9A-HJ-NP-Z]?[0-9A-HJ-NP-Z]?\d{9}$/, maxLength: 11, exampleFormat: "FR 12345678901", vatPlaceholder: "99999999999" },
  EL: { regex: /^\d{9}$/, maxLength: 9, exampleFormat: "EL 123456789", vatPlaceholder: "999999999" },
  IT: { regex: /^\d{11}$/, maxLength: 11, exampleFormat: "IT 12345678901", vatPlaceholder: "99999999999" },
  HR: { regex: /^\d{11}$/, maxLength: 11, exampleFormat: "HR 12345678901", vatPlaceholder: "99999999999" },
  LV: { regex: /^\d{11}$/, maxLength: 11, exampleFormat: "LV 12345678901", vatPlaceholder: "99999999999" },
  LT: { regex: /^\d{9,12}$/, minLength: 9, maxLength: 12, exampleFormat: "LT 123456789 or LT 123456789012", vatPlaceholder: "999999999999" },
  LU: { regex: /^\d{8}$/, maxLength: 8, exampleFormat: "LU 12345678", vatPlaceholder: "99999999" },
  MT: { regex: /^\d{8}$/, maxLength: 8, exampleFormat: "MT 12345678", vatPlaceholder: "99999999" },
  NL: { regex: /^\d{9}[bB]\d{2}$/, maxLength: 12, exampleFormat: "NL 123456789B01", vatPlaceholder: "999999999B99" },
  XI: { regex: /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/, maxLength: 12, exampleFormat: "XI 123456789 or XI GD123", vatPlaceholder: "999999999999" },
  PL: { regex: /^\d{10}$/, maxLength: 10, exampleFormat: "PL 1234567890", vatPlaceholder: "9999999999" },
  PT: { regex: /^\d{9}$/, maxLength: 9, exampleFormat: "PT 123456789", vatPlaceholder: "999999999" },
  RO: { regex: /^\d{2,10}$/, minLength: 2, maxLength: 10, exampleFormat: "RO 12 or RO 1234567890", vatPlaceholder: "9999999999" },
  SK: { regex: /^\d{10}$/, maxLength: 10, exampleFormat: "SK 1234567890", vatPlaceholder: "9999999999" },
  SI: { regex: /^\d{8}$/, maxLength: 8, exampleFormat: "SI 12345678", vatPlaceholder: "99999999" },
  GB: { regex: /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/, maxLength: 12, exampleFormat: "GB 123456789 or GB GD123", vatPlaceholder: "999999999999" },
  SE: { regex: /^\d{12}$/, maxLength: 12, exampleFormat: "SE 123456789012", vatPlaceholder: "999999999999" },
  CZ: { regex: /^\d{8,10}$/, minLength: 8, maxLength: 10, exampleFormat: "CZ 12345678 or CZ 1234567890", vatPlaceholder: "9999999999" },
  DE: { regex: /^\d{9}$/, maxLength: 9, exampleFormat: "DE 123456789", vatPlaceholder: "999999999" },
  HU: { regex: /^\d{8}$/, maxLength: 8, exampleFormat: "HU 12345678", vatPlaceholder: "99999999" },
  AT: { regex: /^U\d{8}$/, maxLength: 9, exampleFormat: "AT U12345678", vatPlaceholder: "U99999999" },
  CY: { regex: /^\d{8}[a-zA-Z]$/, maxLength: 9, exampleFormat: "CY 12345678X", vatPlaceholder: "99999999X" },
  IE: { regex: /^\d[a-zA-Z0-9]\d{5,6}[a-zA-Z]{1,2}$/, minLength: 8, maxLength: 10, exampleFormat: "IE 3A46140B, IE 3746140HH", vatPlaceholder: "9X9999XX" },
  ES: { regex: /^([A-Z]\d{8}|\d{8}[A-Z]|[A-Z]\d{7}[A-Z])$/, maxLength: 9, exampleFormat: "ES B12345678 / ES N1234567X / ES 12345678Z", vatPlaceholder: "X9999999X" }
};

export const validateVatNumber = (countryCode, vatNumber) => {
  const format = vatFormats[countryCode];
  if (!format) return true;
  const exampleFormat = vatFormats[countryCode]?.exampleFormat || "";
  const { regex, minLength, maxLength } = format;
  if (!regex.test(vatNumber)) {
    if (vatNumber?.length < minLength || vatNumber?.length > maxLength) {
      return `Invalid VAT number format. It should be between ${minLength} and ${maxLength} digits long.`;
    }
    return `Invalid VAT number format. Expected format: ${exampleFormat}`;
  }
  return true;
};

export const vatPlaceholder = (countryCode) => {
  const vatPlaceholder = vatFormats[countryCode]?.vatPlaceholder || "Vat. Number";
  return vatPlaceholder;
};